
import Debounce from '@/decorators/debounce';
import SessionListElement from '@/model/SessionListElement';
import api from '@/service/api';
import { polishPlurals } from 'polish-plurals';
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';

interface ClientData {
    linkCode: string;
    fullName: string;
    email: string;
    claimedAt: number;
    photographerComment: string;
}

@Component
export default class SingleSummary extends Vue {
    public polishPlurals = polishPlurals;

    get isSessionFullySelected() {
        const sessionClientIds = this.clients.map(client => client.linkCode);
        const selectedSessionClients = this.selectedClientIds.filter((id) => sessionClientIds.includes(id));
        return selectedSessionClients.length === sessionClientIds.length && sessionClientIds.length > 0;
    }

    get isSessionPartiallySelected() {
        const sessionClientIds = this.clients.map((client) => client.linkCode);
        const selectedSessionClients = this.selectedClientIds.filter((id) => sessionClientIds.includes(id));
        return selectedSessionClients.length > 0 && selectedSessionClients.length < sessionClientIds.length;
    }

    get localClients() {
        const groupedByEmail = this.clients.reduce((acc, client) => {
            const normalizedEmail = (client.email || '').trim().toLowerCase();
            if (!normalizedEmail) { return acc; }
            if (!acc[normalizedEmail]) {
                acc[normalizedEmail] = [];
            }
            acc[normalizedEmail].push(client);
            return acc;
        }, {} as { [email: string]: ClientData[] });

        const formatClaimedAt = (claimedAt: number) => {
            const claimedAtDate = new Date(claimedAt * 1000);
            const daysAgo = Math.ceil(((new Date()).getTime() - claimedAtDate.getTime()) / (1000 * 60 * 60 * 24));
            return `${
                Intl.DateTimeFormat('pl', {
                    dateStyle: 'short',
                } as Intl.DateTimeFormatOptions, // https://github.com/microsoft/TypeScript/issues/38266
            ).format(claimedAtDate)} (${daysAgo} ${this.polishPlurals('dzień', 'dni', 'dni', daysAgo)} temu)`;
        };

        return Object.values(groupedByEmail)
            .map((group) => {
                const uniqueNames = Array.from(new Set(
                    group
                        .map((client) => (client.fullName || '').trim())
                        .filter((fullName) => fullName.length > 0),
                ));
                const uniqueLogins = Array.from(new Set(
                    group
                        .map((client) => formatClaimedAt(client.claimedAt))
                        .filter((claimedAt) => claimedAt.length > 0),
                ));
                const representativeClient = group[0];
                const firstNonEmptyComment = group.find((client) => (client.photographerComment || '').trim().length > 0);
                return {
                    ...representativeClient,
                    linkCodes: group.map((client) => client.linkCode),
                    fullName: uniqueNames.join(', '),
                    claimedAt: uniqueLogins.join(', '),
                    photographerComment: firstNonEmptyComment ? firstNonEmptyComment.photographerComment : representativeClient.photographerComment,
                };
            })
            .map((client, index) => ({ ...client, rowNumber: index + 1 }));
    }
    @Prop({
        type: Object,
        required: true,
    })
    session!: SessionListElement;

    @Prop({
        type: Array,
        required: true,
    })
    clients!: Array<ClientData>;

    expanded: Array<ClientData> = [];

    headers = [
        { text: '', value: 'rowNumber', width: 30, sortable: false },
        { text: '', value: 'select', width: 40 },
        {
            text: 'Klient',
            align: 'start',
            sortable: true,
            value: 'fullName',
            width: '25%',
        },
        { text: 'Adres email', value: 'email', width: '25%' },
        { text: 'Logowanie', value: 'claimedAt', width: '20%' },
        { text: 'Kontakt z klientem', value: 'contact' },
        { text: 'Historia płatności', value: 'payments' },
        { text: 'Komentarz', value: 'data-table-expand', width: '100' },
    ];

    comments: { [linkCode: string]: string } = {};
    oldComments: { [linkCode: string]: string } = {};
    selectedClientIds: string[] = [];
    sessionSelected = false;

    mounted() {
        this.comments = Object.values(this.clients).reduce((prev, client) => ({
            ...prev,
            [client.linkCode]: client.photographerComment
        }), {});
        this.oldComments = { ...this.comments };
        this.updateSessionCheckboxState();
    }

    @Watch('clients', { deep: true })
    onClientsChanged() {
        this.updateSessionCheckboxState();
    }

    @Watch('selectedClientIds', { deep: true })
    onSelectedClientIdsChanged() {
        this.sessionSelected = this.isSessionFullySelected;
    }

    @Watch('comments', {
        deep: true
    })
    @Debounce(1000)
    async updateComment(newValue: { [code: string]: string }) {
        const codes = Object.keys(this.oldComments);
        for (const code of codes) {
            if (newValue[code] !== this.oldComments[code]) {
                await api.updateClientComment(code, newValue[code]);
                (this as any).$vToastify.success({
                    title: 'Komentarz zakutalizowany',
                });
            }
        }
        this.oldComments = { ...this.comments };
    }

    openChat(client: ClientData) {
        // TODO: Implement chat functionality
        console.log('Opening chat with:', client.fullName, client.email);
    }

    openInvoices(client: ClientData) {
        // TODO: Implement invoices functionality
        console.log('Opening invoices for:', client.fullName, client.email);
    }

    onSessionSelectionChange() {
        const sessionClientIds = this.clients.map((client) => client.linkCode);

        if (this.sessionSelected) {
            // Select all clients in this session
            const newSelectedIds = [...new Set([...this.selectedClientIds, ...sessionClientIds])];
            this.selectedClientIds = newSelectedIds;
        } else {
            // Deselect all clients in this session
            this.selectedClientIds = this.selectedClientIds.filter(id => !sessionClientIds.includes(id));
        }
    }

    updateSessionCheckboxState() {
        this.sessionSelected = this.isSessionFullySelected;
    }

    isClientSelected(client: ClientData & { linkCodes?: string[] }) {
        const ids = client.linkCodes || [client.linkCode];
        return ids.every(id => this.selectedClientIds.includes(id));
    }

    setClientSelected(client: ClientData & { linkCodes?: string[] }, selected: boolean) {
        const ids = client.linkCodes || [client.linkCode];
        if (selected) {
            this.selectedClientIds = Array.from(new Set([...this.selectedClientIds, ...ids]));
            return;
        }
        this.selectedClientIds = this.selectedClientIds.filter(id => !ids.includes(id));
    }

    isExpanded(client: any) {
        return this.expanded.some(expandedClient => expandedClient.linkCode === client.linkCode);
    }

    toggleExpanded(client: any) {
        if (this.isExpanded(client)) {
            this.expanded = this.expanded.filter(c => c.linkCode !== client.linkCode);
        } else {
            this.expanded = [...this.expanded, client];
        }
    }
}
